import { defineStore } from 'pinia'
import { User, ItemPermissions, UserLimits, UserMetadata, UserUsage, UserSort, UserFoundDataFile } from '@webapp/gp/GroupAdminModel'
import { useGroupAdminApi } from './GroupAdminApi'
import { AsyncData, LoadMode } from '@webapp/util/AsyncData'
import { AsyncDataStore, VersionedKeyList } from '@webapp/util/AsyncDataStore'
import { useDataGroupStore } from './DataGroupStore'
import { useSubscriptionStore } from './SubscriptionStore'
import { useUserSupervisorStore } from './UserSupervisorStore'
import { usePermissions } from './Permissions'
import { useTokenManager, UserPrivilege } from '@webapp/auth/TokenManager'
import { DateTime } from 'luxon'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { assignExisting, isDefined } from '@webapp/util/TypeScriptUtil'
import { PaginatedList, PaginatedListOptions, PatchChange } from '@webapp/util/Api'
import { MetricData, metricTotal } from '@webapp/util/Metrics'
import { uniqBy } from 'lodash'

export const useUserStore = defineStore('users', () => {
    const asyncUsers = new Map<string, AsyncData<User>>()
    const asyncDataGroupMemberKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncFamilyGroupMemberKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncFamilyGroupInviteKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncSubscriptionKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncPaymentMethodKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncSupervisorKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncSupervisedUserKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncPermissions = new Map<string, AsyncData<ItemPermissions>>()
    const asyncUsage = new Map<string, AsyncData<UserUsage>>()
    const asyncFoundDataFiles = new Map<string, AsyncData<UserFoundDataFile[]>>()

    const gp = useGroupAdminApi()
    const dataGroupStore = useDataGroupStore()
    const subscriptionStore = useSubscriptionStore()
    const userSupervisorStore = useUserSupervisorStore()
    const tokenManager = useTokenManager()
    const { getAsyncPermissionsCore } = usePermissions()

    function getAsyncSelf(loadMode = LoadMode.TrackOnly) {
        return getAsyncUser(tokenManager.current.userId, loadMode)
    }
    
    function getAsyncUser(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncUserList([id], loadMode)[0] : undefined
    }

    function getAsyncUserList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        ids.forEach(checkNoAlias)
        const store = useUserStore() // use to get/return reactive store objects
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            const plainItems = await gp.getPlainByIdsAsync("users", ids, 'profileDataGroup')
            console.log(`Loaded ${plainItems.length} users by id: ${plainItems.map(p => p.id).join(", ")}`)
            return processExpandedUsers(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncUsers, ids, "User", loadMode, loadAsync)
    }

    function getLoadedUser(id: string | undefined) {
        return getAsyncUser(id)?.data
    }

    function getLoadedUserList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        return getAsyncUserList(ids, loadMode).map(a => a.data).filter(isDefined)
    }

    async function getUserListAsync(ids: (string | undefined)[]) {
        return (await Promise.all(getAsyncUserList(ids, LoadMode.EnsureLoaded).map(a => a.whenLoadCompleted)))
            .filter(isDefined)
    }

    async function searchUsersAsync(options?: UserSearchOptions) {
        const requestTime = DateTime.utc()
        const args = {
            q: options?.name,
            sort: options?.sort,
            pageSize: options?.pageSize,
            next: options?.next,
            expand: 'items/usage,items/subscriptions,items/supervisors'
        }
        const plainPage = await gp.getPlainAsync("users", args) as PaginatedList<any>
        console.log(`Loaded page of ${plainPage.items.length} users`)
        const users = addUsersToStore(plainPage.items, requestTime)
        return { items: [...users], nextPageToken: plainPage.nextPageToken } as PaginatedList<User>
    }

    async function getUserCountAsync() {
        return metricTotal(await gp.getPlainAsync("metrics/users") as MetricData[])
    }

    function getAsyncPermissions(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useUserStore() // use to get/return reactive store objects
        return getAsyncPermissionsCore(store.asyncPermissions, id, "User", "users", loadMode)
    }

    async function getPermissionsAsync(id: string | undefined) {
        return (await getAsyncPermissions(id, LoadMode.EnsureLoaded)?.whenLoadCompleted) ?? ItemPermissions.None
    }

    async function getPrivilegesAsync(id: string) {
        const p = await gp.getPlainAsync(`users/${id}/privileges`)
        return (p as string).split(',').map(s => s.trim()) as UserPrivilege[]
    }

    function getAsyncUsage(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useUserStore() // use to get/return reactive store objects
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            const plainItem = await gp.getPlainAsync(`users/${ids[0]}/usage`)
            console.log(`Loaded usage for user ${ids[0]}`)
            plainItem.userId = ids[0] // not in response but needed for caching
            return AsyncDataStore.createItemMap([plainItem], "UserUsage", p => assignExisting(new UserUsage(), p), u => u.userId!)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncUsage, [id], 'UserUsage', loadMode, loadAsync).at(0)
    }

    function getAsyncFoundDataFiles(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useUserStore()
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            const plainItems = await gp.getPlainCollectionAsync(`users/${ids[0]}/founddatafiles`)
            console.log(`Loaded ${plainItems.length} found data files for user ${ids[0]}`)
            const items = plainItems.map((p: any) => assignExisting(new UserFoundDataFile(), p))
            return new Map([[ids[0], items]])
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncFoundDataFiles, [id], 'UserFoundDataFiles', loadMode, loadAsync).at(0)
    }

    async function addForProfileAsync(profileGroupId: string) {
        const store = useUserStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const p = await gp.postPlainAsync(`users`, undefined, { profile: profileGroupId })
            return plainToUser(p)
        }            
        const user = await AsyncDataStore.addToStoreMapAsync(store.asyncUsers, "User", u => u.id, addCoreAsync)

        const asyncProfileGroup = dataGroupStore.asyncDataGroups.get(profileGroupId)
        AsyncData.invalidate(asyncProfileGroup) // group should now have isUserProfile set to true
        const asyncSupervisedKeyList = store.asyncSupervisedUserKeys.get(tokenManager.current.userId!)
        AsyncData.invalidate(asyncSupervisedKeyList) // should have new entry for the new user
        
        return user
    }
    
    async function patchAsync(id: string, changes: PatchChange[]) {
        checkNoAlias(id)
        const store = useUserStore() // use to get/return reactive store objects
        const patchUserAsync = async (id: string, changes: PatchChange[]) => {
            const p = await gp.patchPlainAsync(`users/${id}`, changes);
            return plainToUser(p);
        }
        await AsyncDataStore.patchFromStoreAsync(store.asyncUsers, id, "User", changes, patchUserAsync)
    }

    async function setLimitsDisabledAsync(id: string, value: boolean) {
        await gp.postPlainAsync(`users/${id}/limitsDisabled`, undefined, { value })
        getAsyncUser(id, LoadMode.Reload) // trigger reload, but don't wait for it
    }
    
    async function resetAsync(id: string) {
        await gp.postPlainAsync(`users/${id}/reset`)
        getAsyncUser(id, LoadMode.Reload) // trigger reload, but don't wait for it
    }

    async function mergeFoundDataFiles(id: string, foundDataFiles: UserFoundDataFile[]) {
        const plainItems = foundDataFiles.map(foundDataFileToPlain)
        await gp.postPlainAsync(`users/${id}/foundDataFiles`, plainItems, { mode: 'merge'})
    }

    function addUsersToStore(plainUsers: any[], requestTime: DateTime) {
        const store = useUserStore() // use to get/return reactive store objects
        const userMap = processExpandedUsers(uniqBy(plainUsers, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncUsers, "User", userMap, requestTime)
        return userMap.values()
    }

    function checkNoAlias(id: string | undefined) {
        // use of aliases could result in multiple (possibly inconsistent) copies of a item in the store under different keys
        if (id == "$me") {
            throw 'UserStore.AliasNotAllowed';
        }
    }

    function plainToUser(p: any) {
        const user = assignExisting(new User(), p)
        user.limitsDisabled = p.limitsDisabled ?? false
        user.limits = assignExisting(new UserLimits(), p.limits)
        user.createdDate = DateTimeUtil.fromAPI(p.createdDate)
        user.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
        user.metadata = p.metadata ? assignExisting(new UserMetadata(), p.metadata) : undefined
        return user
    }

    function plainToUserUsage(p: any) {
        const usage = assignExisting(new UserUsage(), p)
        usage.lastSyncDate = DateTimeUtil.fromAPI(p.lastSyncDate)
        return usage
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function processExpandedUsers(plainUsers: any[], requestTime: DateTime) {
        // check length to avoid unnecessary recursion (users can expand data groups and vice versa)
        if (plainUsers.length) {
            const store = useUserStore() // use to get/return reactive store objects

            const expandedProfileUsers = plainUsers.filter(u => u.profileDataGroup)
            if (expandedProfileUsers.length) {
                const ownerLookup = new Map(expandedProfileUsers.map(u => [u.profileDataGroup, u.id as string]))
                dataGroupStore.addDataGroupsToStore(expandedProfileUsers.map(u => u.profileDataGroup), requestTime, p => ownerLookup.get(p)!)
            }

            addUsageToStore(plainUsers.map(p => p.usage).filter(isDefined), requestTime)

            for (const u of plainUsers.filter(u => u.subscriptions)) {
                const subMap = subscriptionStore.addSubscriptionsToStore(u.subscriptions, requestTime)
                const subIds = [...subMap.values()].map(s => s.id!)
                
                AsyncDataStore.addKeyListToStore(store.asyncSubscriptionKeys, u.id, "User.SubscriptionKeys", subIds, requestTime)
            }

            for (const u of plainUsers.filter(u => u.supervisors)) {
                const superMap = userSupervisorStore.addUserSupervisorsToStore(u.supervisors, requestTime)
                const superIds = [...superMap.values()].map(s => s.id!)
                
                AsyncDataStore.addKeyListToStore(store.asyncSupervisorKeys, u.id, "User.SupervisorKeys", superIds, requestTime)
            }
        }
        return AsyncDataStore.createItemMap(plainUsers, "User", plainToUser, u => u.id!)
    }

    function addUsageToStore(plainUsages: any[], requestTime: DateTime) {
        const store = useUserStore() // use to get/return reactive store objects
        const usageMap = AsyncDataStore.createItemMap(plainUsages, "UserUsage", plainToUserUsage, u => u.userId!)
        AsyncDataStore.addItemMapToStore(store.asyncUsage, "UserUsage", usageMap, requestTime)
    }

    function foundDataFileToPlain(fd: UserFoundDataFile) {
        const plain = Object.assign({} as any, fd)
        plain.modifiedDate = DateTimeUtil.toAPI(fd.modifiedDate)
        plain.foundDate = DateTimeUtil.toAPI(fd.foundDate)
        return plain
    }

    return {
        asyncUsers,
        asyncDataGroupMemberKeys,
        asyncFamilyGroupMemberKeys,
        asyncFamilyGroupInviteKeys,
        asyncSubscriptionKeys,
        asyncPaymentMethodKeys,
        asyncSupervisorKeys,
        asyncSupervisedUserKeys,
        asyncPermissions,
        asyncUsage,
        asyncFoundDataFiles,
        getAsyncSelf,
        getAsyncUser,
        getAsyncUserList,
        getLoadedUser,
        getLoadedUserList,
        getUserListAsync,
        searchUsersAsync,
        getUserCountAsync,
        getAsyncPermissions,
        getPermissionsAsync,
        getPrivilegesAsync,
        getAsyncUsage,
        getAsyncFoundDataFiles,
        addForProfileAsync,
        patchAsync,
        setLimitsDisabledAsync,
        resetAsync,
        mergeFoundDataFiles,
        addUsersToStore,
    }
})

export interface UserSearchOptions extends PaginatedListOptions {
    name?: string,
    sort?: UserSort,
}
